<template>
    <div class="floor-area mt-3">
        <div v-if="hasBuildingPartsSpecified()">
            <template v-for="(_, key) in buildingParts" :key="key">
                <BuildingPartControl :state="buildingParts[key]"
                                     :roomLists="roomLists"
                                     :ref="key"
                                     :validate="validate"
                                     @clone="cloneBuildingPart(key)"
                                     @remove="removeBuildingPart(key)"
                />
            </template>
        </div>

        <div class="mt-3">
            <div class="row">
                <div>
                    <button class="btn btn-outline-primary" @click="addBuildingPart()">
                        <StairsIcon class="input-icon"/>
                        {{ $t('form.floorArea.buttonAddBuildingPart') }}
                    </button>
                </div>
            </div>
        </div>

        <div class="mt-4">
            <div class="row mb-2">
                <div class="col-6 col-lg-5 pe-4 align-content-center">
                    <label for="floor-area-net form-label">{{ $t('floorArea.netArea') }}</label>
                </div>
                <div class="col-6 col-lg-3">
                    <div class="input-group">
                        <input id="floor-area-net"
                               type="text"
                               :disabled="hasBuildingPartsSpecified()"
                               v-model="netAreaRaw"
                               class="form-control"
                               :class="{'is-invalid': isInvalid() && netArea === null}"
                        >
                        <span class="input-group-text">m<sup>2</sup></span>
                    </div>
                    <span class="invalid-feedback"
                          v-if="isInvalid() && ! hasBuildingPartsSpecified()"
                    >{{ $t('form.errors.invalidFloorArea') }}</span>
                </div>
            </div>
            <div class="row">
                <div class="col-6 col-lg-5 pe-4 align-content-center">
                    <label for="floor-area-gross form-label">{{ $t('floorArea.grossArea') }}</label>
                </div>
                <div class="col-6 col-lg-3">
                    <div class="input-group">
                        <input
                          id="floor-area-gross"
                          type="text"
                          :disabled="hasBuildingPartsSpecified()"
                          v-model="grossAreaRaw"
                          :class="{'is-invalid': isInvalid() && (grossArea === null || isGrossAreaTooLow())}"
                          class="form-control w-50"
                        >
                        <span class="input-group-text">m<sup>2</sup></span>
                    </div>
                    <template v-if="isInvalid() && ! hasBuildingPartsSpecified()">
                        <div class="invalid-feedback d-block" v-if="isGrossAreaTooLow()">
                          {{ $t('form.errors.grossAreaTooLow') }}
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import {Control} from '@/composables/Control';
import {StairsIcon} from '@/icons';
import {FloorAreaParameter} from '@/model/api/parameters';
import {BuildingPartControlState} from '@/model/state/BuildingPartControlState';
import {RoomControlState} from '@/model/state/RoomControlState';
import {insertAfterKey, isPositiveNumber, parseDecimalNumber} from '@/utils';
import {cloneDeep, fromPairs, round} from 'lodash-es';
import {v4 as uuid} from 'uuid';
import {computed, defineComponent, onMounted, PropType, ref, watch} from 'vue';
import BuildingPartControl from './BuildingPartControl.vue';
import RoomControl from './RoomControl.vue';
import {BuildingPart, RoomList} from './values';

type FloorAreaControlValue = FloorAreaParameter['value'];

export default defineComponent({
    components: {BuildingPartControl, RoomControl, StairsIcon},
    props: {
        value: {
            type: [Array, Object] as PropType<FloorAreaControlValue | null>,
            default: null,
        },
        validate: {
            type: Boolean,
            required: true,
        },
        label: {
            type: String,
            required: true,
        },
        roomLists: {
            type: Object as PropType<{ [listId: string]: RoomList }>,
            required: true,
        },
    },
    emits: ['input', 'validated'],
    setup(props, {emit}) {
        const grossAreaRaw = ref('');
        const netAreaRaw = ref('');
        const buildingParts = ref<{ [key: string]: BuildingPartControlState }>({});

        const grossArea = computed((): number | null => {
            if (grossAreaRaw.value === '') {
                return netArea.value;
            }

            const gross = parseDecimalNumber(grossAreaRaw.value);

            return isPositiveNumber(gross) ? gross : null;
        });

        const netArea = computed((): number | null => {
            const net = parseDecimalNumber(netAreaRaw.value);

            return isPositiveNumber(net) ? net : null;
        });

        function hasBuildingPartsSpecified(): boolean {
            return Object.keys(buildingParts.value).length > 0;
        }

        const currentValue = computed((): FloorAreaControlValue | null => {
            if (! hasBuildingPartsSpecified()) {
                const gross = grossArea.value;
                const net = netArea.value;

                if (gross === null || net === null || gross < net) {
                    return null;
                }

                return {gross, net};
            }

            const parts = Object.values(buildingParts.value).map(partState => partState.value);

            if (parts.includes(null)) {
                return null;
            }

            return parts as BuildingPart[];
        });

        function getValue(): FloorAreaControlValue | null {
            return currentValue.value;
        }

        function validateCurrentValue(): boolean {
            return currentValue.value !== null;
        }

        const {updateValue, isInvalid} = Control<FloorAreaControlValue>(props, emit, {
            getValue,
            validateCurrentValue,
        });

        // mounted logic
        onMounted(() => {
            const value = props.value;

            if (Array.isArray(value)) {
                buildingParts.value = fromPairs(value.map(part => [uuid(), BuildingPartControlState.fromValue(part)]));
                return;
            }

            if (value !== null) {
                grossAreaRaw.value = '' + value.gross;
                netAreaRaw.value = '' + value.net;
            }
        });

        watch(currentValue, () => {
            updateValue();
        });

        watch(buildingParts, () => {
            if (! hasBuildingPartsSpecified()) {
                grossAreaRaw.value = netAreaRaw.value = '';
                return;
            }

            const rooms = Object.values(buildingParts.value)
                .reduce((accumulator: RoomControlState[], part) => [...accumulator, ...Object.values(part.rooms)], []);

            grossAreaRaw.value = calculateArea(rooms);
            netAreaRaw.value = calculateArea(rooms.filter((r) => ! r.isAddition));
        }, {deep: true});

        function calculateArea(rooms: RoomControlState[]): string {
            return '' + round(rooms.reduce((area, room) => area + room.area, 0), 2);
        }

        function addBuildingPart(): void {
            buildingParts.value = Object.assign({}, buildingParts.value, {[uuid()]: new BuildingPartControlState()});
        }

        function cloneBuildingPart(index: string): void {
            buildingParts.value = insertAfterKey(
                buildingParts.value, index, uuid(), cloneDeep(buildingParts.value[index]),
            );
        }

        function removeBuildingPart(index: string): void {
            const parts = Object.assign({}, buildingParts.value);

            delete parts[index];

            buildingParts.value = parts;
        }

        function isGrossAreaTooLow(): boolean {
            const gross = grossArea.value;
            const net = netArea.value;

            return gross !== null && net !== null && gross < net;
        }

        return {
            grossAreaRaw,
            netAreaRaw,
            buildingParts,
            grossArea,
            netArea,
            hasBuildingPartsSpecified,
            addBuildingPart,
            cloneBuildingPart,
            removeBuildingPart,
            isGrossAreaTooLow,
            isInvalid,
        };
    },
});
</script>
